/**
 * Enhanced Telemetry Manager
 * 
 * Orchestrates scenario tracking with DataBagProperties only at completion.
 * Collects data throughout user journey and reports all data at scenario end.
 */

import logger from "./telemetry";
import { logDev as debugLog, warnDev } from '@/logging/devConsole';

type WizardStep = "welcome" | "device-selection" | "auth-setup" | "company-portal" | "app-download";

const AUTH_REDIRECT_STEP = "auth-redirect";
const STEP_KEY_MAP: Record<WizardStep, string> = {
  "welcome": "welcomeTimeMs",
  "device-selection": "deviceSelectionTimeMs",
  "auth-setup": "authSetupTimeMs",
  "company-portal": "companyPortalTimeMs",
  "app-download": "appDownloadTimeMs"
};

const SCENARIO_STORAGE_KEY = "byodWizardScenarioState";
export const DEFAULT_SCENARIO_TIMEOUT_MS = 20 * 60 * 1000; // keep scenario alive for long flows (20 minutes)

interface PersistedScenarioState {
  currentScenario: string;
  currentScenarioId: string;
  collectedData: CollectedData;
  scenarioCompleted: boolean;
  scenarioTimeoutMs?: number;
}

interface CollectedData {
  // Entry source tracking (from URL query param)
  entrySource?: string;

  // User selections and journey data
  deviceType?: "android" | "ios" | "unknown";
  authenticatorStatus?: "set" | "not-set" | "unknown";
  mfaRequired?: boolean;
  mamEnabled?: boolean;
  tenantRegion?: string;
  telemetryEnvironment?: string;
  // Identity captured post-auth
  userInfoTenantId?: string;
  userInfoId?: string;
  
  // Performance metrics
  stepTimings: Record<string, number>;
  stepStartTimes: Record<string, number>;
  completedSteps: string[];
  
  // Loading performance (policy evaluation times)
  mfaEvaluationStartTime?: number;
  mamEvaluationStartTime?: number;
  mfaEvaluationLoadTimeMs?: number;
  mamEvaluationLoadTimeMs?: number;
  
  // Page render performance
  welcomeRenderStartTime?: number;
  welcomeRenderTimeMs?: number;
  deviceSelectionRenderStartTime?: number;
  deviceSelectionRenderTimeMs?: number;
  appDownloadRenderStartTime?: number;
  appDownloadRenderTimeMs?: number;
  
  // Journey tracking
  stepsCompleted: number;
  lastCompletedStep?: string;
  scenarioStartTime?: number;

  // Retry tracking
  retryCounts: Record<string, number>;
  retryErrors: RetryErrorEntry[];

  // Policy evaluation results
  mfaPolicyType?: string;
  mfaPolicyIsRequired?: boolean;
  mfaPolicyReason?: string;
  mfaPolicyDetails?: string;
  mamPolicyType?: string;
  mamPolicyIsRequired?: boolean;
  mamPolicyReason?: string;
  mamPolicyDetails?: string;

  // Failure tracking
  failureReason?: string;
  failureMessage?: string;

  // Token acquisition tracking
  tokenAcquisitionSuccess?: boolean;
  tokenAcquisitionError?: string;
  tokenAcquisitionErrorDetails?: string;

  // Feedback tracking
  feedbackProvided?: boolean;
  feedbackSubmitted?: boolean;
  feedbackCancelled?: boolean;
  feedbackSource?: 'thanks-screen' | 'footer' | 'device-selection';
  feedbackId?: string;
  feedbackTimeOnScreenMs?: number;
  feedbackAuthenticationType?: 'aad' | 'anonymous';
  feedbackErrorMessage?: string; // error message if submission failed
  // Footer feedback specific - tracking complete journey
  footerFeedbackClicked?: boolean;
  footerFeedbackClickCount?: number; // how many times footer link was clicked
  footerFeedbackClickedScreens?: string[]; // array of all screens where footer was clicked
  footerFeedbackCancelledScreens?: Array<{ screen: string, timeMs: number }>; // array of screens with time spent before cancelling
  footerFeedbackSubmittedScreens?: Array<{ screen: string, timeMs: number }>; // array of screens with time spent before submitting
  footerFeedbackCpsPolicyAllowed?: boolean;
}

interface PolicyResultTelemetry {
  policyType?: string | null;
  isRequired?: boolean;
  reason?: string | null;
  details?: string | null;
}
type PolicyKind = "mfa" | "mam";
interface RetryErrorEntry {
  step?: string;
  attempt?: number;
  reason?: string;
  durationMs?: number;
  message?: string;
}

type PolicyFieldConfig = {
  typeKey: keyof CollectedData;
  isRequiredKey: keyof CollectedData;
  reasonKey: keyof CollectedData;
  detailsKey: keyof CollectedData;
};

const POLICY_FIELD_MAP: Record<PolicyKind, PolicyFieldConfig> = {
  mfa: {
    typeKey: "mfaPolicyType",
    isRequiredKey: "mfaPolicyIsRequired",
    reasonKey: "mfaPolicyReason",
    detailsKey: "mfaPolicyDetails"
  },
  mam: {
    typeKey: "mamPolicyType",
    isRequiredKey: "mamPolicyIsRequired",
    reasonKey: "mamPolicyReason",
    detailsKey: "mamPolicyDetails"
  }
};

export class TelemetryManager {
  private currentScenario: string | null = null;
  private currentScenarioId: string | null = null;
  private collectedData: CollectedData = this.createEmptyCollectedData();
  private scenarioCompleted = false;
  private activeLoggerRef: typeof logger | null = null;
  private scenarioCreatedInLogger = false; // Track if scenario was created in current logger
  private scenarioTimeoutMs: number = DEFAULT_SCENARIO_TIMEOUT_MS;
  private lastCompletedScenarioDataBag: any = null; // For testing

  constructor() {
    this.loadPersistedState();
  }

  private ensureLoggerCurrent(): void {
    const loggerChanged = this.activeLoggerRef !== logger;
    
    if (loggerChanged) {
      this.activeLoggerRef = logger;
      this.scenarioCreatedInLogger = false; // Reset flag when logger changes
    }
    
    // If an active scenario exists but hasn't been created in the current logger, rehydrate it
    // This handles both logger changes AND page reloads where state was restored from sessionStorage
    if (this.currentScenario && !this.scenarioCompleted && !this.scenarioCreatedInLogger) {
      this.rehydrateActiveScenario();
    }
  }

  // Guard helper that returns the live scenario name or logs why the request is ignored.
  private ensureScenarioActive(action: string): string | null {
    this.ensureLoggerCurrent();

    if (!this.currentScenario) {
      warnDev(`[TelemetryManager] Cannot ${action} - no active scenario`);
      return null;
    }

    if (this.scenarioCompleted) {
      warnDev(`[TelemetryManager] Cannot ${action} - scenario already completed`);
      return null;
    }

    return this.currentScenario;
  }

  // Safely close any pending step timer when a step transitions.
  private finalizeStepTiming(step: string | undefined, completedAt: number): void {
    if (!step) {
      return;
    }

    const startTime = this.collectedData.stepStartTimes[step];
    if (!startTime || this.collectedData.stepTimings[step]) {
      return;
    }

    this.collectedData.stepTimings[step] = completedAt - startTime;
  }

  // Convert step identifiers into camelCase duration keys for the completion payload.
  private formatStepTimingKey(step: string): string {
    const mappedKey = STEP_KEY_MAP[step as WizardStep];
    if (mappedKey) {
      return mappedKey;
    }

    const parts = step.split(/[-_\s]+/);
    return (
      parts
        .map((part, index) => (index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join("") + "TimeMs"
    );
  }

  private createEmptyCollectedData(): CollectedData {
    return {
      stepTimings: {},
      stepStartTimes: {},
      completedSteps: [],
      stepsCompleted: 0,
      retryCounts: {},
      retryErrors: []
    };
  }

  private withActiveScenario(action: string, updater: () => void): boolean {
    if (!this.ensureScenarioActive(action)) {
      return false;
    }

    updater();
    this.persistState();
    return true;
  }

  /**
   * Start a new scenario - no DataBagProperties, just framework tracking
   */
  startScenario(scenarioName: string, options?: { timeoutMs?: number }): void {
    this.ensureLoggerCurrent();

    if (this.currentScenario && !this.scenarioCompleted) {
      warnDev('[TelemetryManager] Scenario already active, ignoring start request');
      return;
    }

    const timeoutMs = options?.timeoutMs ?? DEFAULT_SCENARIO_TIMEOUT_MS;

    this.currentScenario = scenarioName;
    this.currentScenarioId = this.generateScenarioId();
    this.collectedData = {
      ...this.createEmptyCollectedData(),
      scenarioStartTime: Date.now()
    };
    this.scenarioCompleted = false;
    this.scenarioCreatedInLogger = false; // Reset flag for new scenario
    this.scenarioTimeoutMs = timeoutMs;

    // Use framework's scenario.create - no custom data
    try {
      logger.scenario.create({
        name: scenarioName,
        qualifier: scenarioName,
        timeout: this.scenarioTimeoutMs
      });
      this.scenarioCreatedInLogger = true;
    } catch (error) {
      warnDev('[TelemetryManager] Failed scenario.create', error);
    }

    this.persistState();
    
    debugLog('[TelemetryManager] Started scenario:', scenarioName);
  }

  /**
   * Mark a scenario step - no DataBagProperties, just step tracking
   */
  markStep(step: string): void {
    this.ensureLoggerCurrent();

    const scenarioName = this.ensureScenarioActive(`mark step "${step}"`);
    if (!scenarioName) {
      return;
    }

    // Prevent duplicate step marking
    if (this.collectedData.lastCompletedStep === step) {
      debugLog('[TelemetryManager] Step already marked:', step);
      return;
    }

    const now = Date.now();
    this.finalizeStepTiming(this.collectedData.lastCompletedStep, now);

    // Start timing for new step
    this.collectedData.stepStartTimes[step] = now;
    
    // Update journey tracking
    this.collectedData.stepsCompleted++;
    this.collectedData.lastCompletedStep = step;
    if (!this.collectedData.completedSteps.includes(step)) {
      this.collectedData.completedSteps.push(step);
    }

    // Use framework's scenario.mark - no custom data
    try {
      logger.scenario.mark({ name: scenarioName, qualifier: scenarioName, step });
    } catch (error) {
      warnDev('[TelemetryManager] Failed scenario.mark', error);
    }

    this.persistState();
    debugLog('[TelemetryManager] Marked step:', step);
  }

  /**
   * Complete scenario with ALL collected DataBagProperties
   */
  completeScenario(status: "success" | "failure" | "abandoned", reason?: string): void {
    this.ensureLoggerCurrent();

    if (!this.currentScenario) {
      warnDev('[TelemetryManager] Cannot complete scenario - no active scenario');
      return;
    }

    if (this.scenarioCompleted) {
      warnDev('[TelemetryManager] Scenario already completed');
      return;
    }

    // Mark as completed FIRST to prevent race conditions with abandonment tracking
    this.scenarioCompleted = true;

    // Build final DataBagProperties with all collected data
    const dataBagProperties = this.buildFinalDataBag(status, reason);
    
    // Store for testing
    this.lastCompletedScenarioDataBag = dataBagProperties;
    
    // Complete scenario with framework - pass DataBagProperties as nested object
    try {
      logger.scenario.complete({
        name: this.currentScenario,
        qualifier: this.currentScenario,
        status: status,
        data: {
          DataBagProperties: dataBagProperties
        }
      });
    } catch (error) {
      warnDev('[TelemetryManager] Failed scenario.complete', error);
    }
    
    debugLog('[TelemetryManager] Completed scenario:', this.currentScenario, 'Status:', status);
    debugLog('[TelemetryManager] Final DataBagProperties:', dataBagProperties);

    this.clearPersistedState();
  }

  /**
   * Build final DataBagProperties with all collected data
   */
  private buildFinalDataBag(status: "success" | "failure" | "abandoned", reason?: string): Record<string, any> {
    const now = Date.now();
    const totalTime = this.collectedData.scenarioStartTime ? now - this.collectedData.scenarioStartTime : 0;

    this.finalizeStepTiming(this.collectedData.lastCompletedStep, now);

    const dataBag: Record<string, any> = {
      stepsCompleted: this.collectedData.stepsCompleted,
      totalWizardTimeMs: totalTime,
      lastCompletedStep: this.collectedData.lastCompletedStep,
      scenarioId: this.currentScenarioId
    };

    if (this.collectedData.entrySource) {
      dataBag.entrySource = this.collectedData.entrySource;
    }

    if (this.collectedData.deviceType) {
      dataBag.deviceType = this.collectedData.deviceType;
    }

    if (this.collectedData.authenticatorStatus !== undefined) {
      dataBag.authenticatorStatus = this.collectedData.authenticatorStatus;
    }

    if (this.collectedData.mfaRequired !== undefined) {
      dataBag.mfaRequired = this.collectedData.mfaRequired;
    }

    if (this.collectedData.mamEnabled !== undefined) {
      dataBag.mamEnabled = this.collectedData.mamEnabled;
    }

    if (this.collectedData.tenantRegion) {
      dataBag.tenantRegion = this.collectedData.tenantRegion;
    }
    if (this.collectedData.telemetryEnvironment) {
      dataBag.telemetryEnvironment = this.collectedData.telemetryEnvironment;
    }

    if (Object.keys(this.collectedData.stepTimings).length > 0) {
      Object.entries(this.collectedData.stepTimings).forEach(([step, duration]) => {
        if (step === AUTH_REDIRECT_STEP) {
          return;
        }
        dataBag[this.formatStepTimingKey(step)] = duration;
      });
    }

    if (AUTH_REDIRECT_STEP in this.collectedData.stepTimings) {
      dataBag.authRedirectOccurred = true;
      dataBag.authRedirectDurationMs = this.collectedData.stepTimings[AUTH_REDIRECT_STEP];
    }

    if (this.collectedData.retryCounts) {
      const mfaRetries = this.collectedData.retryCounts['retry-mfa'];
      const mamRetries = this.collectedData.retryCounts['retry-mam'];

      if (typeof mfaRetries === 'number' && mfaRetries > 0) {
        dataBag.mfaRetryAttempts = mfaRetries;
      }

      if (typeof mamRetries === 'number' && mamRetries > 0) {
        dataBag.mamRetryAttempts = mamRetries;
      }
    }

    const retryErrors = this.collectedData.retryErrors
      .map((entry) => ({
        step: entry.step,
        attempt: entry.attempt,
        reason: entry.reason,
        durationMs: entry.durationMs,
        message: entry.message
      }))
      .filter((entry) => Object.values(entry).some((value) => value !== undefined));

    if (retryErrors.length > 0) {
      dataBag.retryErrors = retryErrors;
    }

    this.appendPolicyResult(dataBag, 'mfa');
    this.appendPolicyResult(dataBag, 'mam');

    // Add policy evaluation load times
    if (this.collectedData.mfaEvaluationLoadTimeMs !== undefined) {
      dataBag.mfaEvaluationLoadTimeMs = this.collectedData.mfaEvaluationLoadTimeMs;
    }

    if (this.collectedData.mamEvaluationLoadTimeMs !== undefined) {
      dataBag.mamEvaluationLoadTimeMs = this.collectedData.mamEvaluationLoadTimeMs;
    }

    // Add page render times
    if (this.collectedData.welcomeRenderTimeMs !== undefined) {
      dataBag.welcomeRenderTimeMs = this.collectedData.welcomeRenderTimeMs;
    }

    if (this.collectedData.deviceSelectionRenderTimeMs !== undefined) {
      dataBag.deviceSelectionRenderTimeMs = this.collectedData.deviceSelectionRenderTimeMs;
    }

    if (this.collectedData.appDownloadRenderTimeMs !== undefined) {
      dataBag.appDownloadRenderTimeMs = this.collectedData.appDownloadRenderTimeMs;
    }

    // Add token acquisition data
    if (this.collectedData.tokenAcquisitionSuccess !== undefined) {
      dataBag.tokenAcquisitionSuccess = this.collectedData.tokenAcquisitionSuccess;
    }

    if (this.collectedData.tokenAcquisitionError !== undefined) {
      dataBag.tokenAcquisitionError = this.collectedData.tokenAcquisitionError;
    }

    if (this.collectedData.tokenAcquisitionErrorDetails !== undefined) {
      dataBag.tokenAcquisitionErrorDetails = this.collectedData.tokenAcquisitionErrorDetails;
    }

    // Add feedback data (from footer feedback during wizard or post-wizard feedback)
    if (this.collectedData.footerFeedbackClicked !== undefined) {
      dataBag.footerFeedbackClicked = this.collectedData.footerFeedbackClicked;
    }

    if (this.collectedData.footerFeedbackClickCount !== undefined) {
      dataBag.footerFeedbackClickCount = this.collectedData.footerFeedbackClickCount;
    }

    if (this.collectedData.footerFeedbackClickedScreens && this.collectedData.footerFeedbackClickedScreens.length > 0) {
      dataBag.footerFeedbackClickedScreens = this.collectedData.footerFeedbackClickedScreens;
    }

    if (this.collectedData.footerFeedbackCancelledScreens && this.collectedData.footerFeedbackCancelledScreens.length > 0) {
      dataBag.footerFeedbackCancelledScreens = this.collectedData.footerFeedbackCancelledScreens;
    }

    if (this.collectedData.footerFeedbackSubmittedScreens && this.collectedData.footerFeedbackSubmittedScreens.length > 0) {
      dataBag.footerFeedbackSubmittedScreens = this.collectedData.footerFeedbackSubmittedScreens;
    }

    if (this.collectedData.footerFeedbackCpsPolicyAllowed !== undefined) {
      dataBag.footerFeedbackCpsPolicyAllowed = this.collectedData.footerFeedbackCpsPolicyAllowed;
    }

    if (this.collectedData.feedbackProvided !== undefined) {
      dataBag.feedbackProvided = this.collectedData.feedbackProvided;
    }

    if (this.collectedData.feedbackSubmitted !== undefined) {
      dataBag.feedbackSubmitted = this.collectedData.feedbackSubmitted;
    }

    if (this.collectedData.feedbackCancelled !== undefined) {
      dataBag.feedbackCancelled = this.collectedData.feedbackCancelled;
    }

    if (this.collectedData.feedbackSource) {
      dataBag.feedbackSource = this.collectedData.feedbackSource;
    }

    if (this.collectedData.feedbackId) {
      dataBag.feedbackId = this.collectedData.feedbackId;
    }

    if (this.collectedData.feedbackTimeOnScreenMs !== undefined) {
      dataBag.feedbackTimeOnScreenMs = this.collectedData.feedbackTimeOnScreenMs;
    }

    if (this.collectedData.feedbackAuthenticationType) {
      dataBag.feedbackAuthenticationType = this.collectedData.feedbackAuthenticationType;
    }

    if (this.collectedData.feedbackErrorMessage) {
      dataBag.feedbackErrorMessage = this.collectedData.feedbackErrorMessage;
    }

    if (status === "success") {
      // Scenario completed successfully at AppDownloadScreen (feedback screens are UserBI only)
      dataBag.scenarioStatus = 'success';
      dataBag.wizardCompleted = true;  // Automatic - wizard is complete when scenario succeeds
    } else if (status === "abandoned" && reason) {
      dataBag.abandonmentReason = reason;
      dataBag.timeToAbandonmentMs = totalTime;
      dataBag.scenarioStatus = 'abandoned';
    } else if (status === "failure" && reason) {
      dataBag.errorReason = reason;
      dataBag.timeToFailureMs = totalTime;
      dataBag.scenarioStatus = 'failure';
      if (this.collectedData.failureMessage) {
        dataBag.errorMessage = this.collectedData.failureMessage;
      }
    }

    return dataBag;
  }

  /**
   * Recreate active scenario in the logger after telemetry reinitialization.
   * Ensures mid-journey environment switches do not lose the qualifier.
   */
  rehydrateActiveScenario(): void {
    // Detect if the logger instance actually changed
    const loggerChanged = this.activeLoggerRef !== logger;
    this.activeLoggerRef = logger;

    // If logger changed, we need to recreate the scenario in the new logger
    if (loggerChanged) {
      this.scenarioCreatedInLogger = false;
    }

    if (!this.currentScenario || this.scenarioCompleted || this.scenarioCreatedInLogger) {
      return;
    }

    try {
      logger.scenario.create({
        name: this.currentScenario,
        qualifier: this.currentScenario,
        timeout: this.scenarioTimeoutMs
      });
      this.scenarioCreatedInLogger = true;
    } catch (error) {
      warnDev('[TelemetryManager] Failed scenario.create during rehydrate', error);
      return;
    }

    this.replayCompletedSteps();
  }

  /**
   * Check if there's an active scenario
   */
  hasActiveScenario(): boolean {
    return this.currentScenario !== null && !this.scenarioCompleted;
  }

  /**
   * Get current scenario name
   */
  getCurrentScenario(): string | null {
    return this.currentScenario;
  }

  /**
   * Collect entry source from URL query parameter
   */
  collectEntrySource(source: string): void {
    if (!this.withActiveScenario('collect entry source', () => {
      this.collectedData.entrySource = source;
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected entry source:', source);
  }

  /**
   * Collect device selection data
   */
  collectDeviceSelection(deviceType: "android" | "ios"): void {
    if (!this.withActiveScenario('collect device selection', () => {
      this.collectedData.deviceType = deviceType;
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected device selection:', deviceType);
  }

  /**
   * Collect authentication setup data
   */
  collectAuthStatus(status: "set" | "not-set" | "unknown", mfaRequired?: boolean): void {
    if (!this.withActiveScenario('collect auth status', () => {
      this.collectedData.authenticatorStatus = status;
      if (mfaRequired !== undefined) {
        this.collectedData.mfaRequired = mfaRequired;
      }
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected auth status:', status, 'MFA required:', mfaRequired);
  }

  /**
   * Collect company portal (MAM) data
   */
  collectCompanyPortalStatus(mamEnabled: boolean): void {
    if (!this.withActiveScenario('collect company portal status', () => {
      this.collectedData.mamEnabled = mamEnabled;
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected company portal status - MAM:', mamEnabled);
  }

  collectTenantRegion(region: {
    region?: string | null;
    environment?: string | null;
  }): void {
    if (!this.withActiveScenario('collect tenant region', () => {
      this.collectedData.tenantRegion = this.sanitizeTelemetryString(region.region ?? undefined);
      this.collectedData.telemetryEnvironment = region.environment
        ? this.sanitizeTelemetryString(region.environment)
        : undefined;
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected tenant region data:', {
      region: this.collectedData.tenantRegion,
      environment: this.collectedData.telemetryEnvironment
    });
  }

  /** Collect user identity values */
  collectUserIdentity(identity: { tenantId?: string | null; userId?: string | null }): void {
    if (!this.withActiveScenario('collect user identity', () => {
      if (identity.tenantId) {
        this.collectedData.userInfoTenantId = this.sanitizeTelemetryString(identity.tenantId);
      }
      if (identity.userId) {
        this.collectedData.userInfoId = this.sanitizeTelemetryString(identity.userId);
      }
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected user identity:', {
      tenantId: this.collectedData.userInfoTenantId,
      userId: this.collectedData.userInfoId
    });
  }

  /** Retrieve currently collected (selected) device type for UserBI enrichment */
  getDeviceType(): "android" | "ios" | "unknown" | undefined {
    return this.collectedData.deviceType;
  }

  /** Retrieve last completed scenario step for UserBI enrichment */
  getLastCompletedStep(): string | undefined {
    return this.collectedData.lastCompletedStep;
  }

  /**
   * Record retry attempt details for MFA/MAM flows
   */
  recordRetryAttempt(step: 'retry-mfa' | 'retry-mam', attempt: number): void {
    if (!this.withActiveScenario('record retry attempt', () => {
      const current = this.collectedData.retryCounts[step] ?? 0;
      this.collectedData.retryCounts[step] = Math.max(current, attempt);
    })) {
      return;
    }

    debugLog('[TelemetryManager] Recorded retry attempt:', step, 'attempt:', attempt);
  }

  /**
   * Start timing for policy evaluation (MFA or MAM)
   * Called when loading screen shows and API call starts
   */
  startPolicyEvaluation(step: 'mfa' | 'mam'): void {
    if (!this.withActiveScenario('start policy evaluation', () => {
      const now = performance.now();
      if (step === 'mfa') {
        this.collectedData.mfaEvaluationStartTime = now;
      } else {
        this.collectedData.mamEvaluationStartTime = now;
      }
      debugLog('[TelemetryManager] Started policy evaluation timing:', step, now);
    })) {
      return;
    }
  }

  /**
   * End timing for policy evaluation (MFA or MAM)
   * Called when next screen is rendered after evaluation completes
   */
  endPolicyEvaluation(step: 'mfa' | 'mam'): void {
    if (!this.withActiveScenario('end policy evaluation', () => {
      const now = performance.now();
      if (step === 'mfa' && this.collectedData.mfaEvaluationStartTime) {
        const duration = Math.round(now - this.collectedData.mfaEvaluationStartTime);
        this.collectedData.mfaEvaluationLoadTimeMs = duration;
        debugLog('[TelemetryManager] MFA evaluation completed in', duration, 'ms');
        delete this.collectedData.mfaEvaluationStartTime;
      } else if (step === 'mam' && this.collectedData.mamEvaluationStartTime) {
        const duration = Math.round(now - this.collectedData.mamEvaluationStartTime);
        this.collectedData.mamEvaluationLoadTimeMs = duration;
        debugLog('[TelemetryManager] MAM evaluation completed in', duration, 'ms');
        delete this.collectedData.mamEvaluationStartTime;
      }
    })) {
      return;
    }
  }

  /**
   * Start timing for page render
   * Called at the start of component render
   */
  startPageRender(screen: 'welcome' | 'device-selection' | 'app-download'): void {
    if (!this.withActiveScenario('start page render', () => {
      const now = performance.now();
      if (screen === 'welcome') {
        this.collectedData.welcomeRenderStartTime = now;
      } else if (screen === 'device-selection') {
        this.collectedData.deviceSelectionRenderStartTime = now;
      } else {
        this.collectedData.appDownloadRenderStartTime = now;
      }
      debugLog('[TelemetryManager] Started page render timing:', screen, now);
    })) {
      return;
    }
  }

  /**
   * End timing for page render
   * Called when page is fully rendered (in useEffect)
   */
  endPageRender(screen: 'welcome' | 'device-selection' | 'app-download'): void {
    if (!this.withActiveScenario('end page render', () => {
      const now = performance.now();
      if (screen === 'welcome' && this.collectedData.welcomeRenderStartTime) {
        const duration = Math.round(now - this.collectedData.welcomeRenderStartTime);
        this.collectedData.welcomeRenderTimeMs = duration;
        debugLog('[TelemetryManager] Welcome render completed in', duration, 'ms');
        delete this.collectedData.welcomeRenderStartTime;
      } else if (screen === 'device-selection' && this.collectedData.deviceSelectionRenderStartTime) {
        const duration = Math.round(now - this.collectedData.deviceSelectionRenderStartTime);
        this.collectedData.deviceSelectionRenderTimeMs = duration;
        debugLog('[TelemetryManager] Device selection render completed in', duration, 'ms');
        delete this.collectedData.deviceSelectionRenderStartTime;
      } else if (screen === 'app-download' && this.collectedData.appDownloadRenderStartTime) {
        const duration = Math.round(now - this.collectedData.appDownloadRenderStartTime);
        this.collectedData.appDownloadRenderTimeMs = duration;
        debugLog('[TelemetryManager] App download render completed in', duration, 'ms');
        delete this.collectedData.appDownloadRenderStartTime;
      }
    })) {
      return;
    }
  }
  
  recordRecoverableError(reason: string, step?: 'retry-mfa' | 'retry-mam' | 'mfa-call' | 'mam-call', attempt?: number, error?: unknown, durationMs?: number): void {
    const message = this.normalizeErrorMessage(error);

    if (!this.withActiveScenario('record recoverable error', () => {
      this.storeRetryErrorEntry({
        step,
        attempt,
        reason,
        durationMs,
        message
      });
    })) {
      return;
    }

    debugLog('[TelemetryManager] Recorded recoverable error:', {
      reason,
      step,
      attempt,
      durationMs,
      message
    });
  }

  collectMfaPolicyResult(result: PolicyResultTelemetry): void {
    if (!this.withActiveScenario('collect MFA policy result', () => {
      this.updatePolicyResult('mfa', result);
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected MFA policy result:', {
      type: this.collectedData.mfaPolicyType,
      isRequired: this.collectedData.mfaPolicyIsRequired,
      reason: this.collectedData.mfaPolicyReason
    });
  }

  collectMamPolicyResult(result: PolicyResultTelemetry): void {
    if (!this.withActiveScenario('collect MAM policy result', () => {
      this.updatePolicyResult('mam', result);
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected MAM policy result:', {
      type: this.collectedData.mamPolicyType,
      isRequired: this.collectedData.mamPolicyIsRequired,
      reason: this.collectedData.mamPolicyReason
    });
  }

  /**
   * Capture failure metadata so the completion payload can contain richer diagnostics.
   */
  recordFailure(reason: string, error?: unknown): void {
    if (!this.withActiveScenario('record failure', () => {
      this.collectedData.failureReason = reason;
      this.collectedData.failureMessage = this.normalizeErrorMessage(error);
    })) {
      return;
    }

    debugLog('[TelemetryManager] Recorded failure details for reason:', reason, 'message:', this.collectedData.failureMessage);
  }

  /**
   * Collect app download data
   */
  collectAppDownloadData(data: { platform?: string | null }): void {
    if (!this.withActiveScenario('collect app download data', () => {
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected app download data - Platform:', data.platform);
  }

  /**
   * Collect token acquisition result
   */
  collectTokenAcquisition(success: boolean, error?: string, errorDetails?: string): void {
    if (!this.withActiveScenario('collect token acquisition', () => {
    })) {
      return;
    }

    this.collectedData.tokenAcquisitionSuccess = success;
    if (success) {
      debugLog('[TelemetryManager] Collected token acquisition success');
    } else {
      this.collectedData.tokenAcquisitionError = error;
      this.collectedData.tokenAcquisitionErrorDetails = errorDetails;
      debugLog('[TelemetryManager] Collected token acquisition error:', error);
    }

    this.persistState();
  }

  /**
   * Collect footer feedback click data
   */
  collectFooterFeedbackClick(screen: string, cpsPolicyAllowed?: boolean): void {
    if (!this.withActiveScenario('collect footer feedback click', () => {
      this.collectedData.footerFeedbackClicked = true;

      // Track click count
      this.collectedData.footerFeedbackClickCount = (this.collectedData.footerFeedbackClickCount || 0) + 1;

      // Append to array of screens where clicked
      if (!this.collectedData.footerFeedbackClickedScreens) {
        this.collectedData.footerFeedbackClickedScreens = [];
      }
      this.collectedData.footerFeedbackClickedScreens.push(screen);

      if (cpsPolicyAllowed !== undefined) {
        this.collectedData.footerFeedbackCpsPolicyAllowed = cpsPolicyAllowed;
      }
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected footer feedback click - Screen:', screen, 'Click count:', this.collectedData.footerFeedbackClickCount, 'CPS allowed:', cpsPolicyAllowed);
    this.persistState();
  }

  /**
   * Collect feedback completion data
   */
  collectFeedbackCompletion(data: {
    feedbackProvided: boolean;
    feedbackSubmitted?: boolean;
    feedbackCancelled?: boolean;
    feedbackSource?: 'thanks-screen' | 'footer' | 'device-selection';
    feedbackId?: string;
    timeOnScreenMs?: number;
    authenticationType?: 'aad' | 'anonymous';
    errorMessage?: string;
    currentScreen?: string; // which screen the feedback was shown from (for footer feedback)
    timeSpentMs?: number; // time spent on footer feedback modal before submit/cancel
  }): void {
    if (!this.withActiveScenario('collect feedback completion', () => {
      this.collectedData.feedbackProvided = data.feedbackProvided;
      if (data.feedbackSubmitted !== undefined) {
        this.collectedData.feedbackSubmitted = data.feedbackSubmitted;
      }
      if (data.feedbackCancelled !== undefined) {
        this.collectedData.feedbackCancelled = data.feedbackCancelled;
      }
      if (data.feedbackSource) {
        this.collectedData.feedbackSource = data.feedbackSource;
      }
      if (data.feedbackId) {
        this.collectedData.feedbackId = data.feedbackId;
      }
      if (data.timeOnScreenMs !== undefined) {
        this.collectedData.feedbackTimeOnScreenMs = data.timeOnScreenMs;
      }
      if (data.authenticationType) {
        this.collectedData.feedbackAuthenticationType = data.authenticationType;
      }
      if (data.errorMessage) {
        this.collectedData.feedbackErrorMessage = data.errorMessage;
      }

      // Track footer feedback journey - where user cancelled or submitted, with time spent
      if (data.feedbackSource === 'footer' && data.currentScreen) {
        const timeMs = data.timeSpentMs ?? 0; // default to 0 if not provided
        if (data.feedbackCancelled) {
          // User cancelled - track which screen they cancelled from and time spent
          if (!this.collectedData.footerFeedbackCancelledScreens) {
            this.collectedData.footerFeedbackCancelledScreens = [];
          }
          this.collectedData.footerFeedbackCancelledScreens.push({ screen: data.currentScreen, timeMs });
        } else if (data.feedbackSubmitted) {
          // User submitted - track which screen they submitted from and time spent (supports multiple submissions)
          if (!this.collectedData.footerFeedbackSubmittedScreens) {
            this.collectedData.footerFeedbackSubmittedScreens = [];
          }
          this.collectedData.footerFeedbackSubmittedScreens.push({ screen: data.currentScreen, timeMs });
        }
      }
    })) {
      return;
    }

    debugLog('[TelemetryManager] Collected feedback completion:', {
      provided: data.feedbackProvided,
      submitted: data.feedbackSubmitted,
      source: data.feedbackSource,
      id: data.feedbackId,
      error: data.errorMessage
    });
    this.persistState();
  }

  /**
   * Track auth redirect timing
   */
  trackAuthRedirect(started: boolean): void {
    if (!this.ensureScenarioActive('track auth redirect')) {
      return;
    }

    const now = Date.now();
    if (started) {
      // Store auth redirect state in sessionStorage for immediate access
      sessionStorage.setItem('authRedirectInProgress', 'true');
      
      // Also dispatch event for event-based listeners
      const event = new CustomEvent('authRedirectStateChange', {
        detail: { started: true }
      });
      window.dispatchEvent(event);
      
      this.collectedData.stepStartTimes[AUTH_REDIRECT_STEP] = now;
      debugLog('[TelemetryManager] Auth redirect started - abandonment tracking disabled');
    } else {
      // Clear auth redirect state
      sessionStorage.removeItem('authRedirectInProgress');
      
      // Calculate redirect duration
      const startTime = this.collectedData.stepStartTimes[AUTH_REDIRECT_STEP];
      if (startTime) {
        this.collectedData.stepTimings[AUTH_REDIRECT_STEP] = now - startTime;
      }
      
      // Re-enable abandonment tracking
      const event = new CustomEvent('authRedirectStateChange', {
        detail: { started: false }
      });
      window.dispatchEvent(event);
      
      debugLog('[TelemetryManager] Auth redirect completed, duration:', this.collectedData.stepTimings[AUTH_REDIRECT_STEP], '- abandonment tracking re-enabled');
    }

    this.persistState();
  }

  /**
   * Reset manager state (for testing)
   */
  reset(): void {
    this.clearPersistedState();
  }
  private generateScenarioId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `scenario-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private sanitizeTelemetryString(value: unknown): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const stringValue = typeof value === 'string' ? value : String(value);
    return stringValue.replace(/\s+/g, ' ').trim().slice(0, 512);
  }

  private normalizeErrorMessage(error: unknown): string | undefined {
    if (error === null || error === undefined) {
      return undefined;
    }

    if (typeof error === 'string') {
      return this.sanitizeTelemetryString(error);
    }

    // Handle Error objects specially - extract key properties
    if (error instanceof Error) {
      const errorObj: Record<string, any> = {
        message: error.message,
        name: error.name,
      };
      
      // Include any additional properties (like status, data from RTK Query errors)
      for (const key in error) {
        if (key !== 'message' && key !== 'name' && key !== 'stack') {
          errorObj[key] = (error as any)[key];
        }
      }
      
      try {
        return this.sanitizeTelemetryString(JSON.stringify(errorObj));
      } catch (stringifyError) {
        debugLog('[TelemetryManager] Failed to stringify Error object', stringifyError);
      }
    }

    // Handle plain objects (like RTK Query error responses)
    try {
      const stringified = JSON.stringify(error);
      if (typeof stringified === 'string') {
        return this.sanitizeTelemetryString(stringified);
      }
    } catch (stringifyError) {
      debugLog('[TelemetryManager] Failed to stringify error, falling back to String()', stringifyError);
    }

    return this.sanitizeTelemetryString(String(error));
  }

  private updatePolicyResult(kind: PolicyKind, result: PolicyResultTelemetry): void {
    const mapping = POLICY_FIELD_MAP[kind];

    this.setPolicyField(mapping.typeKey, this.sanitizeTelemetryString(result.policyType ?? undefined));
    this.setPolicyField(mapping.reasonKey, this.sanitizeTelemetryString(result.reason ?? undefined));
    this.setPolicyField(mapping.detailsKey, this.sanitizeTelemetryString(result.details ?? undefined));

    if (typeof result.isRequired === 'boolean') {
      this.setPolicyField(mapping.isRequiredKey, result.isRequired);
    }
  }

  private appendPolicyResult(dataBag: Record<string, any>, kind: PolicyKind): void {
    const mapping = POLICY_FIELD_MAP[kind];

    const typeValue = this.collectedData[mapping.typeKey];
    if (typeof typeValue === 'string') {
      dataBag[mapping.typeKey] = typeValue;
    }

    const isRequiredValue = this.collectedData[mapping.isRequiredKey];
    if (typeof isRequiredValue === 'boolean') {
      dataBag[mapping.isRequiredKey] = isRequiredValue;
    }

    const reasonValue = this.collectedData[mapping.reasonKey];
    if (typeof reasonValue === 'string') {
      dataBag[mapping.reasonKey] = reasonValue;
    }

    const detailsValue = this.collectedData[mapping.detailsKey];
    if (typeof detailsValue === 'string') {
      dataBag[mapping.detailsKey] = detailsValue;
    }
  }

  private setPolicyField<T extends keyof CollectedData>(key: T, value: CollectedData[T]): void {
    this.collectedData[key] = value;
  }

  private storeRetryErrorEntry(entry: RetryErrorEntry): void {
    this.collectedData.retryErrors.push({
      step: entry.step,
      attempt: entry.attempt,
      reason: this.sanitizeTelemetryString(entry.reason),
      durationMs: entry.durationMs,
      message: this.sanitizeTelemetryString(entry.message)
    });
  }

  private cloneCollectedData(data: CollectedData): CollectedData {
    return {
      ...data,
      stepTimings: { ...data.stepTimings },
      stepStartTimes: { ...data.stepStartTimes },
      completedSteps: [...data.completedSteps],
      retryCounts: { ...data.retryCounts },
      retryErrors: data.retryErrors.map(entry => ({ ...entry }))
    };
  }

  private persistState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    if (!this.currentScenario || !this.currentScenarioId || this.scenarioCompleted) {
      sessionStorage.removeItem(SCENARIO_STORAGE_KEY);
      return;
    }

    const state: PersistedScenarioState = {
      currentScenario: this.currentScenario,
      currentScenarioId: this.currentScenarioId,
      collectedData: this.cloneCollectedData(this.collectedData),
      scenarioCompleted: this.scenarioCompleted,
      scenarioTimeoutMs: this.scenarioTimeoutMs
    };

    try {
      sessionStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      warnDev('[TelemetryManager] Failed to persist scenario state', error);
    }
  }

  private loadPersistedState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const raw = sessionStorage.getItem(SCENARIO_STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const state = JSON.parse(raw) as PersistedScenarioState;
      if (!state.currentScenario || !state.currentScenarioId || state.scenarioCompleted) {
        sessionStorage.removeItem(SCENARIO_STORAGE_KEY);
        return;
      }

      this.currentScenario = state.currentScenario;
      this.currentScenarioId = state.currentScenarioId;

      const restoredCollectedData = state.collectedData ?? this.createEmptyCollectedData();

      this.collectedData = this.cloneCollectedData({
        ...this.createEmptyCollectedData(),
        ...restoredCollectedData
      });
      this.scenarioCompleted = false;
      this.scenarioTimeoutMs = state.scenarioTimeoutMs ?? DEFAULT_SCENARIO_TIMEOUT_MS;

      // Don't create scenario here - let ensureLoggerCurrent handle it on first use
      // This avoids accessing logger before it's initialized (circular dependency)
      debugLog('[TelemetryManager] Restored persisted scenario:', this.currentScenario, 'with scenarioId:', this.currentScenarioId);
    } catch (error) {
      warnDev('[TelemetryManager] Failed to load persisted scenario state', error);
      sessionStorage.removeItem(SCENARIO_STORAGE_KEY);
    }
  }

  private clearPersistedState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(SCENARIO_STORAGE_KEY);
    this.currentScenario = null;
    this.currentScenarioId = null;
    this.collectedData = this.createEmptyCollectedData();
    this.scenarioCompleted = false;
    this.scenarioCreatedInLogger = false;
    this.scenarioTimeoutMs = DEFAULT_SCENARIO_TIMEOUT_MS;
  }

  // Rehydrate scenario marks after redirects so servers see the full step history.
  private replayCompletedSteps(): void {
    const scenarioName = this.currentScenario;
    if (!scenarioName || this.collectedData.completedSteps.length === 0) {
      return;
    }

    this.collectedData.completedSteps.forEach((step) => {
      try {
        logger.scenario.mark({
          name: scenarioName,
          qualifier: scenarioName,
          step
        });
      } catch (error) {
        warnDev('[TelemetryManager] Failed scenario.mark during replay', error);
      }
    });
  }
}

// Export singleton instance
export const telemetryManager = new TelemetryManager();